import React, { useState, useEffect } from 'react';
import { useApiService } from '../../services/api';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  TrendingDown,
  RefreshCw,
  Calendar,
  BookOpen,
  Clock,
  Edit3
} from 'lucide-react';
import DailyLedgerForm from './DailyLedgerForm';

const DailyLedger = () => {
  const apiService = useApiService();
  const [dailyLedger, setDailyLedger] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadDailyLedger();
    loadStats();
  }, [currentPage, searchTerm, filters]);

  const loadDailyLedger = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...filters
      };
      
      const response = await apiService.getAllDailyLedger(params);
      setDailyLedger(response.dailyLedger || []);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Error loading daily ledger:', error);
      alert('Failed to load daily ledger entries');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const params = { ...filters };
      const response = await apiService.getDailyLedgerStats(params);
      setStats(response);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      await apiService.deleteDailyLedger(id);
      await loadDailyLedger();
      await loadStats();
    } catch (err) {
      alert('Failed to delete entry. Please try again.');
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setShowEditModal(true);
  };

  const handleSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      
      if (editingEntry) {
        await apiService.updateDailyLedger(editingEntry.id, formData);
      } else {
        await apiService.createDailyLedger(formData);
      }
      
      setShowAddModal(false);
      setShowEditModal(false);
      setEditingEntry(null);
      await loadDailyLedger();
      await loadStats();
    } catch (error) {
      console.error('Error saving entry:', error);
      alert('Failed to save entry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: ''
    });
    setCurrentPage(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-PK');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-PK') + ' ' + date.toLocaleTimeString('en-PK', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#fafbfc', minHeight: '100vh', padding: '2rem 1.5rem', width: '100%', boxSizing: 'border-box' }}>
      {/* Header Section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '.5rem' }}>
        <div>
          <h1 className="fw-bold mb-2" style={{ color: '#1f2937', fontSize: '1.75rem', letterSpacing: '-0.025em' }}>Daily Ledger</h1>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
            Track your daily expenses and financial records
          </p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button 
            className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold" 
            style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem' }}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Search size={16} />
            <span className="d-none d-sm-inline">Filter</span>
          </button>
          <button 
            className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold" 
            style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.875rem', boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.1), 0 1px 2px 0 rgba(99, 102, 241, 0.06)' }} 
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={16} />
            <span className="d-none d-sm-inline">Add Entry</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '.5rem' }}>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#fef2f2', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <TrendingDown size={20} style={{ color: '#dc2626' }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total Expenses</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>{formatCurrency(stats.totalExpense || 0)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0f9ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <RefreshCw size={20} style={{ color: '#0284c7' }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total Transactions</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>{stats.totalTransactions || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div style={{ background: 'white', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={clearFilters}
              style={{ padding: '0.5rem 1rem', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', color: '#374151', fontSize: '0.875rem', cursor: 'pointer' }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Search Section */}
      <div className="card border-0 shadow-sm mb-4" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
        <div className="card-body p-3 p-md-4">
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search ledger names or descriptions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', paddingLeft: '16px', fontSize: '0.875rem' }}
                />
              </div>
            </div>
            <div style={{ minWidth: 180, textAlign: 'right', flex: 'none' }}>
              <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                Showing {dailyLedger.length} entries
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Ledger Table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading entries...</div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {/* <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Date</th> */}
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Ledger Name</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Description</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Amount</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>User</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Created</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Updated</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dailyLedger.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280', fontStyle: 'italic' }}>
                    No entries found. Add your first entry to get started.
                  </td>
                </tr>
              ) : (
                dailyLedger.map((entry) => (
                  <tr key={entry.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    {/* <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Calendar size={16} style={{ color: '#9ca3af' }} />
                        {formatDate(entry.transactionDate)}
                      </div>
                    </td> */}
                    <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600' }}>
                        <BookOpen size={16} style={{ color: '#8b5cf6' }} />
                        {entry.ledgerName}
                      </div>
                    </td>
                    <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>{entry.description}</td>
                    <td style={{ padding: '1rem', color: '#dc2626', fontSize: '0.875rem', fontWeight: '700' }}>
                      {formatCurrency(entry.amount)}
                    </td>
                    <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>{entry.user?.name || 'Unknown'}</td>
                    <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Clock size={16} style={{ color: '#9ca3af' }} />
                        {formatDateTime(entry.createdAt)}
                      </div>
                    </td>
                    <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Edit3 size={16} style={{ color: '#9ca3af' }} />
                        {formatDateTime(entry.updatedAt)}
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button 
                          onClick={() => handleEditEntry(entry)}
                          style={{ background: 'none', border: 'none', padding: '0.5rem', borderRadius: '6px', cursor: 'pointer', color: '#6366f1' }}
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          onClick={() => handleDeleteEntry(entry.id)}
                          style={{ background: 'none', border: 'none', padding: '0.5rem', borderRadius: '6px', cursor: 'pointer', color: '#dc2626' }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '2rem' }}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            style={{ background: 'white', border: '1px solid #d1d5db', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s' }}
          >
            Previous
          </button>
          
          <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            style={{ background: 'white', border: '1px solid #d1d5db', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s' }}
          >
            Next
          </button>
        </div>
      )}

      {/* Modals */}
      <DailyLedgerForm
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleSubmit}
        isLoading={isSubmitting}
      />

      <DailyLedgerForm
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingEntry(null);
        }}
        onSubmit={handleSubmit}
        editingEntry={editingEntry}
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default DailyLedger;
